<template>
  <div class="organization-user-modal__overlay">
    <div class="organization-user-modal__background" @click="$emit('closeModal')" />

    <div class="organization-user-modal">
      <div class="organization-user-modal__header">
        <h2 class="organization-user-modal__title">{{ title }}</h2>
        <button
          type="button"
          class="organization-user-modal__close"
          :aria-label="adminText('organizationAll.close_modal_70d3a544', 'Close modal')"
          @click="$emit('closeModal')"
        >
          <v-icon color="#979797" size="24">close</v-icon>
        </button>
      </div>

      <div class="organization-user-modal__fields">
        <text-field
          v-if="showNameFields"
          v-model.trim="form.firstName"
          class="organization-user-modal__field"
          :label="adminText('organizationAll.first_name_b6ea992a', 'First Name')"
          type="text"
          disabled
        />

        <text-field
          v-if="showNameFields"
          v-model.trim="form.lastName"
          class="organization-user-modal__field"
          :label="adminText('organizationAll.last_name_863cb39f', 'Last Name')"
          type="text"
          disabled
        />

        <text-field
          v-model.trim="form.email"
          class="organization-user-modal__field"
          :validator="vs.form.email"
          :disabled="type !== 'CREATE'"
          :label="adminText('organizationAll.e_mail_0d01e2e8', 'E-mail')"
          type="text"
        />

        <select-field
          v-model="form.role"
          class="organization-user-modal__field organization-user-modal__field--select"
          :items="organizationOptions(roles, 'display')"
          :validator="vs.form.role"
          :disabled="isRoleDisabled"
          :label="organizationText(roleLabel)"
          item-render="display"
          item-select="value"
        />

        <select-field-multiple-with-all
          v-if="showEventsField && isTheaterOrganization"
          v-model="form.eventTemplateIds"
          class="organization-user-modal__field"
          :sections="organizationSections(organizationTemplateSections)"
          :disabled="type === 'SENT'"
          :validator="vs.form.eventTemplateIds"
          :error-messages="organizationMessages(eventTemplateIdsErrorMessages)"
          item-render="display"
          item-select="value"
          :label="adminText('organizationAll.select_event_templates_43efaddc', 'Select event templates')"
        />
        <select-field-multiple-with-all
          v-if="showEventsField && !isTheaterOrganization"
          v-model="form.eventIds"
          class="organization-user-modal__field"
          :sections="organizationSections(organizationEventSections)"
          :disabled="type === 'SENT'"
          :validator="vs.form.eventIds"
          :error-messages="organizationMessages(eventIdsErrorMessages)"
          item-render="display"
          item-select="value"
          :label="adminText('organizationAll.select_events_8aa58f1b', 'Select Events')"
        />
      </div>

      <div class="organization-user-modal__actions" :class="actionsClassObject">
        <main-button
          v-if="showDeleteButton"
          flat
          class="organization-user-modal__delete-button"
          color="error"
          @click="$emit('delete')"
        >
          {{ adminText('organizationAll.delete_d6f56360', 'DELETE') }}
        </main-button>

        <main-button
          v-if="showSubmitButton"
          depressed
          class="organization-user-modal__submit-button"
          @click="$emit('submit')"
        >
          {{ organizationText(buttonTitle) }}
        </main-button>
      </div>
    </div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import moment from 'moment'

import textField from '../../shared/inputs/text-field.vue'
import selectField from '../../shared/inputs/select-field.vue'
import selectFieldMultipleWithAll from '../../shared/inputs/select-field-mutliple-with-all.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'organization-user-modal',
  components: {
    textField,
    selectField,
    selectFieldMultipleWithAll,
  },
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    organizationEvents: {
      type: Array,
      default: () => [],
    },
    organizationEventTemplates: {
      type: Array,
      default: () => [],
    },
    isTheaterOrganization: {
      type: Boolean,
      default: false,
    },
    forCreate: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'CREATE',
    },
  },
  data() {
    return {
      form: this.initialValues,
      roles: [
        {
          display: 'Organization Admin',
          value: 'ADMIN',
        },
        {
          display: 'Organization Owner',
          value: 'OWNER',
        },
        {
          display: 'Event Admin',
          value: 'EVENT_ADMIN',
        },
        {
          display: 'Event Viewer',
          value: 'EVENT_VIEWER',
        },
      ],
    }
  },
  computed: {
    title() {
      const titleByType = {
        CREATE: this.adminText('organizationAll.invite_user_30d47462', 'Invite User'),
        UPDATE: this.adminText('organizationAll.update_user_e27738c4', 'Update User'),
        EXPIRED: this.adminText('organizationAll.update_user_e27738c4', 'Update User'),
        SENT: this.adminText('organizationAll.invite_sent_5b5031c7', 'Invite Sent'),
      }

      return titleByType[this.type] || this.adminText('organizationAll.update_user_e27738c4', 'Update User')
    },
    buttonTitle() {
      const buttonTitleByType = {
        CREATE: 'INVITE',
        UPDATE: this.adminText('organizationAll.update_52c6c181', 'UPDATE'),
        EXPIRED: 'RESEND',
      }

      return buttonTitleByType[this.type] || this.adminText('organizationAll.update_52c6c181', 'UPDATE')
    },
    showNameFields() {
      return this.type === 'UPDATE' && this.form.firstName && this.form.lastName
    },
    isEventScopedRole() {
      return ['EVENT_ADMIN', 'EVENT_VIEWER'].includes(this.form.role)
    },
    showEventsField() {
      return this.isEventScopedRole
    },
    showDeleteButton() {
      return this.type !== 'CREATE'
    },
    showSubmitButton() {
      return this.type !== 'SENT'
    },
    isRoleDisabled() {
      return this.type === 'SENT'
    },
    roleLabel() {
      return this.type === 'CREATE' ? this.adminText('organizationAll.select_role_21f7dab8', 'Select Role') : this.adminText('organizationAll.role_c3f104d1', 'Role')
    },
    organizationTemplateSections() {
      const items = this.organizationEventTemplates.map(t => ({
        value: Number(t.id),
        display: t.title,
      }))

      return items.length
        ? [
          {
            title: this.adminText('organizationAll.event_templates_a2428d69', 'Event templates'),
            items,
          },
        ]
        : []
    },
    organizationEventSections() {
      const now = new Date().getTime()
      const eventsForRole =
        this.form.role === 'EVENT_VIEWER'
          ? this.organizationEvents.filter(e => e.extraModules && e.extraModules.ticketing)
          : this.organizationEvents
      const sortedEvents = [...eventsForRole]
        .sort((a, b) => new Date(b.to).getTime() - new Date(a.to).getTime())
        .map(event => ({
          value: event.id,
          display: `${event.title} (${moment(event.to).format('DD.MM.YYYY')})`,
          isLive: new Date(event.to).getTime() >= now,
        }))

      const liveEvents = sortedEvents.filter(event => event.isLive)
      const pastEvents = sortedEvents.filter(event => !event.isLive)

      return [
        {
          title: this.adminText('organizationAll.live_events_928cddff', 'Live Events'),
          items: liveEvents,
        },
        {
          title: this.adminText('organizationAll.past_events_bb0f2468', 'Past Events'),
          items: pastEvents,
        },
      ].filter(section => section.items.length > 0)
    },
    actionsClassObject() {
      return {
        'organization-user-modal__actions--single': !this.showDeleteButton,
      }
    },
    eventIdsErrorMessages() {
      return {
        requiredForEventScopedRole: this.adminText('organizationAll.select_at_least_one_event_for_this_role_7cc27d06', 'Select at least one event for this role.'),
      }
    },
    eventTemplateIdsErrorMessages() {
      return {
        requiredForTheaterScopedRole: this.adminText('organizationAll.select_at_least_one_event_template_for_this_r_871e29a1', 'Select at least one event template for this role.'),
      }
    },
  },
  watch: {
    'form.role'(value) {
      if (!['EVENT_ADMIN', 'EVENT_VIEWER'].includes(value)) {
        this.form.eventIds = []
        this.form.eventTemplateIds = []
      } else if (value === 'EVENT_VIEWER') {
        this.pruneNonTicketingEventIds()
      }
    },
    organizationEvents: {
      handler() {
        if (this.form.role === 'EVENT_VIEWER') {
          this.pruneNonTicketingEventIds()
        }
      },
      deep: true,
    },
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
  },
  methods: {
    pruneNonTicketingEventIds() {
      const ticketingIds = new Set(
        (this.organizationEvents || [])
          .filter(e => e.extraModules && e.extraModules.ticketing)
          .map(e => e.id),
      )
      this.form.eventIds = (this.form.eventIds || []).filter(id => ticketingIds.has(Number(id)))
    },
  },
}
</script>

<style scoped>
.organization-user-modal__overlay {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.organization-user-modal__background {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.32);
}

.organization-user-modal {
  position: relative;
  z-index: 1;
  width: calc(100% - 2rem);
  max-width: 492px;
  padding: 2rem;
  border-radius: 20px;
  background: #ffffff;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.organization-user-modal::-webkit-scrollbar {
  display: none;
}

.organization-user-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.organization-user-modal__title {
  margin: 0;
  color: #0277bd;
  font-family: 'Raleway';
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.organization-user-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.organization-user-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 2rem;
}

.organization-user-modal__field {
  margin: 0 0 0.5rem 0 !important;
}

.organization-user-modal__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.organization-user-modal__actions--single {
  justify-content: flex-end;
}

.organization-user-modal__delete-button {
  margin: 0 !important;
}

.organization-user-modal__submit-button {
  margin: 0 !important;
}

@media screen and (max-width: 700px) {
  .organization-user-modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .organization-user-modal__delete-button,
  .organization-user-modal__submit-button {
    width: 100%;
  }
}
</style>
